import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export const LIBRO_PAGE_SIZES = [10, 20, 25, 50, 100];

export function paginateList(items, page, pageSize) {
  const list = items || [];
  const total = list.length;
  const ps = LIBRO_PAGE_SIZES.includes(pageSize) ? pageSize : 25;
  const totalPages = Math.max(1, Math.ceil(total / ps) || 1);
  const p = Math.min(Math.max(1, page || 1), totalPages);
  const start = (p - 1) * ps;
  return {
    items: list.slice(start, start + ps),
    pagination: {
      page: p,
      page_size: ps,
      total,
      total_pages: totalPages,
    },
  };
}

/** Paginación en cliente para listas ya cargadas (mismo UI que libros). */
export function useClientPagination(items, resetKey = "", initialPageSize = 25) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    LIBRO_PAGE_SIZES.includes(initialPageSize) ? initialPageSize : 25,
  );

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const { items: pageItems, pagination } = useMemo(
    () => paginateList(items, page, pageSize),
    [items, page, pageSize],
  );

  useEffect(() => {
    if (pagination.page !== page) setPage(pagination.page);
  }, [pagination.page, page]);

  return {
    pageItems,
    pagination,
    pageSize,
    setPage,
    setPageSize: (n) => {
      setPageSize(n);
      setPage(1);
    },
  };
}

export function LibroPagination({
  pagination,
  pageSize,
  onPageSizeChange,
  onPageChange,
  testidPrefix = "libro",
}) {
  if (!pagination || pagination.total <= 0) return null;

  const from = (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border bg-background"
      data-testid={`${testidPrefix}-pagination`}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Filas por página</span>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="h-8 w-[72px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LIBRO_PAGE_SIZES.map((n) => (
              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span className="text-xs text-muted-foreground">
        {from}–{to} de {pagination.total.toLocaleString("es-CL")}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={pagination.page <= 1}
          onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
          data-testid={`${testidPrefix}-page-prev`}
          className="inline-flex items-center gap-1 border border-border disabled:opacity-40 hover:bg-card text-xs px-2.5 py-1.5 rounded-sm"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Anterior
        </button>
        <span className="text-xs text-muted-foreground px-2">
          {pagination.page} / {pagination.total_pages}
        </span>
        <button
          type="button"
          disabled={pagination.page >= pagination.total_pages}
          onClick={() => onPageChange(pagination.page + 1)}
          data-testid={`${testidPrefix}-page-next`}
          className="inline-flex items-center gap-1 border border-border disabled:opacity-40 hover:bg-card text-xs px-2.5 py-1.5 rounded-sm"
        >
          Siguiente <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
